import { z } from "zod";

// TES-818 punkt 2 — F-04 (veckoincheckningen) as a TIME SERIES.
//
// A-01/F-04: the coach must be able to COMPARE the coaching-relevant answers
// across weeks, "tillsammans med text, datum, källa och original". Until now
// the only surface was CoachPatientDetail.weeklyCheckins — one submission at a
// time, newest first — which answers "what did she say last week?" and cannot
// answer "is this getting better or worse?".
//
// This is a separate read model rather than a flag on the existing payload:
// the trend is a WIDER disclosure than one week's answers (a pattern across
// weeks is legible in a way a single answer is not), so it gets its own shape
// whose redaction can be reasoned about on its own terms.
//
// Two rules are baked into the shape rather than left to the admin:
//
//   1. Submission-level routing is UNCHANGED (TES-690). A check-in that
//      escalated to the doctor is his and never reaches a coach's series.
//   2. Field-level: the medical block never reaches a coach as content. It
//      arrives as ONE aggregated `status_only` series — "the medical questions
//      were answered on these dates" — with no value, no display, no raw.
//
// A doctor reading the same endpoint gets every series as `full`; he already
// reads the raw answers on his own patient page.

// How a series may be shown. `status_only` carries dates and nothing else:
// `value`, `display` and `raw` are always null on its points.
export const checkinTrendDisclosureSchema = z.enum(["full", "status_only"]);
export type CheckinTrendDisclosure = z.infer<
  typeof checkinTrendDisclosureSchema
>;

// What the series can be drawn as. `scale` and `numeric` are plottable (the
// admin draws a line); `choice` and `text` are read in time order.
export const checkinTrendSeriesKindSchema = z.enum([
  "scale",
  "numeric",
  "choice",
  "text",
]);
export type CheckinTrendSeriesKind = z.infer<
  typeof checkinTrendSeriesKindSchema
>;

// The synthetic key of the aggregated medical series. Not a form field — see
// the redaction note in apps/api/src/clinician/checkin-trend.ts for why the
// medical fields collapse into one row instead of one row each.
export const MEDICAL_CHECKIN_SERIES_KEY = "medical_block";

export const checkinTrendPointSchema = z.object({
  submissionId: z.string(),
  submittedAt: z.string(),
  // "Källa" — which form, and which published version of it, the answer came
  // from. Carried per point, not per series: two points can disagree because
  // the question was re-published under the patient between them.
  templateKey: z.string(),
  templateVersion: z.number().int(),
  submittedInLocale: z.string(),
  // Whether the patient answered this field in this submission. A gated
  // follow-up she never saw is `false` — which is why the line must break
  // here rather than plot a zero.
  answered: z.boolean(),
  // The plottable number, when the series has one. Null for choice/text
  // series, for unanswered points, and for every `status_only` point.
  value: z.number().nullable(),
  // The answer as a reader should see it: the option's Swedish label, the
  // free text, or the formatted number. Null when withheld or unanswered.
  display: z.string().nullable(),
  // The ORIGINAL stored answer, so a coach can always get back to what was
  // actually submitted rather than to our rendering of it. Null when withheld.
  raw: z.unknown(),
  // TES-1210 — A-01: each point shows "kopplad coachfritext/återkoppling när
  // sådan finns". When the coach answered this check-in, the time the reply
  // was sent; the admin links the point to the check-in row, which shows the
  // reply. Coach trend only (null for the doctor's), and optional so an
  // older API response still parses.
  coachReplyAt: z.string().nullable().optional(),
});
export type CheckinTrendPoint = z.infer<typeof checkinTrendPointSchema>;

export const checkinTrendSeriesSchema = z.object({
  key: z.string(),
  // The question, in Swedish, from the template that owns the key. Falls back
  // to the raw key when no published version still defines it — the same
  // fallback the per-week answer list uses, so nothing is silently dropped.
  label: z.string(),
  kind: checkinTrendSeriesKindSchema,
  unit: z.string().nullable(),
  // Fixed axis bounds when the question has them (a 1-5 scale always reads
  // 1-5, so a good week and a bad week are the same height every time).
  // Null means the admin derives the bounds from the data.
  min: z.number().nullable(),
  max: z.number().nullable(),
  disclosure: checkinTrendDisclosureSchema,
  // Ascending by submittedAt, one point per included submission — including
  // the submissions where this field was not answered, so every series shares
  // the same dates and they line up when stacked.
  points: z.array(checkinTrendPointSchema),
});
export type CheckinTrendSeries = z.infer<typeof checkinTrendSeriesSchema>;

export const checkinTrendSchema = z.object({
  patientId: z.string(),
  // Series order follows the published template's field order, so the trends
  // read in the same order as the form the patient filled in.
  series: z.array(checkinTrendSeriesSchema),
});
export type CheckinTrend = z.infer<typeof checkinTrendSchema>;
